const configuredSiteUrl = import.meta.env.VITE_SITE_URL || 'https://vicsbricks.com';

export const business = {
  name: "Vic's Bricks",
  legalName: "Vic's Bricks",
  siteUrl: configuredSiteUrl.replace(/\/$/, ''),
  phoneDisplay: '(910) 297-1950',
  phoneHref: 'tel:+19102971950',
  email: 'info@vicsbricks.com',
  emailHref: 'mailto:info@vicsbricks.com',
  base: 'Havelock, North Carolina',
  region: 'Eastern North Carolina',
  trustMarks: ['NCCER-trained', 'Insured'],
  contactEndpoint: import.meta.env.VITE_LEAD_ENDPOINT || import.meta.env.VITE_CONTACT_ENDPOINT || '',
};

export const services = [
  {
    slug: 'brick-repair-repointing',
    name: 'Brick Repair and Repointing',
    navName: 'Brick Repair',
    featured: true,
    footer: true,
    kicker: 'Repair worn joints, damaged brick, and weathered masonry.',
    summary:
      'Careful brick replacement and mortar-joint repair for walls, steps, and other masonry across Eastern North Carolina.',
    intro:
      'Failing mortar joints, loose brick, and weather damage can turn a small masonry problem into a larger repair. The right fix starts with understanding why the masonry is deteriorating.',
    image: '/images/owner-projects/brick-steps-finished.webp',
    imageAlt: 'Finished brick steps rebuilt at an Eastern North Carolina home',
    imagePosition: '50% 50%',
    concernsTitle: 'Signs your brickwork needs a closer look',
    concerns: [
      'Failing mortar joints',
      'Open joints or gaps between brick and mortar',
      'Individual brick that are cracked, loose, or flaking',
      'Previous repairs separating from the original work',
      'Separation near corners, openings, or adjoining materials',
      'Bulging, movement, or cracks that pass through brick',
    ],
    includes: [
      'Selective brick replacement',
      'Mortar-joint repointing',
      'Weather-damage repair',
      'Joint profile and color matching',
      'Selected exterior chimney masonry repairs',
      'Block masonry repair',
    ],
    approach: [
      {
        title: 'Find the contributing problem',
        text: 'Water entry, failed flashing, blocked weeps, movement, and poor drainage can all contribute to masonry damage.',
      },
      {
        title: 'Match the existing assembly',
        text: 'Brick, mortar hardness, sand texture, color, and joint shape should work with the surrounding masonry.',
      },
      {
        title: 'Repair to sound material',
        text: 'Deteriorated mortar is removed to a consistent depth before compatible new mortar is packed and tooled.',
      },
    ],
    projectStoryTitle: 'A wider door opening, rebuilt in brick.',
    projectStoryIntro:
      'This project sequence shows the original opening, selective brick removal, the supported wider opening, and the completed brick surround.',
    projectPhotos: [
      {
        title: 'Before',
        caption: 'The original single-door opening before selective demolition began.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/door-opening-before.webp',
        alt: 'Original single-door opening in a brick wall before widening',
        width: 768,
        height: 1024,
        orientation: 'portrait',
      },
      {
        title: 'Opening the wall',
        caption: 'Brick was removed in a controlled pattern to prepare the wider opening.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/door-opening-demolition.webp',
        alt: 'Brick removed beside a door during a wider-opening project',
        width: 603,
        height: 804,
        orientation: 'portrait',
      },
      {
        title: 'Support and fit',
        caption: 'The wider frame and masonry support were fitted before the surround was finished.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/door-opening-lintel.webp',
        alt: 'Wider exterior door frame with masonry support visible during construction',
        width: 562,
        height: 750,
        orientation: 'portrait',
      },
      {
        title: 'Finished surround',
        caption: 'Brickwork was rebuilt and detailed around the completed wider opening.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/door-opening-finished.webp',
        alt: 'Completed brick surround around a widened exterior door opening',
        width: 562,
        height: 750,
        orientation: 'portrait',
      },
    ],
    faqs: [
      {
        q: 'What is repointing?',
        a: 'Repointing removes deteriorated mortar from the face of a masonry joint and replaces it with compatible new mortar.',
      },
      {
        q: 'Can new mortar match the original?',
        a: 'A sample area can compare color, sand texture, hardness, and joint profile before the broader repair begins.',
      },
      {
        q: 'Will repointing solve every masonry leak?',
        a: 'Not always. Flashing, sealants, caps, weeps, roof transitions, and surrounding drainage may also need attention.',
      },
      {
        q: 'When does a crack need structural evaluation?',
        a: 'Cracks through brick, visible movement, bulging, or recurring separation may need an engineer or another qualified design professional before cosmetic work proceeds.',
      },
    ],
  },
  {
    slug: 'concrete-driveways-patios',
    name: 'Concrete Driveways and Patios',
    navName: 'Concrete',
    featured: true,
    footer: true,
    kicker: 'Flatwork planned for use, grade, drainage, and finish.',
    summary:
      'Concrete driveways, patios, walks, steps, slabs, and equipment pads with site preparation and project-specific curing guidance.',
    intro:
      'A dependable driveway or patio begins below the finished surface. Site access, drainage, expected loads, base preparation, joint layout, finishing, and curing all affect how the concrete performs.',
    image: '/images/owner-projects/concrete-patio-finished.webp',
    imageAlt: 'Finished concrete patio installed behind an Eastern North Carolina home',
    imagePosition: '50% 50%',
    concernsTitle: 'Plan the slab around the property',
    concerns: [
      'Vehicle loads and daily traffic patterns',
      'Water flow across and away from the slab',
      'Soft or previously disturbed subgrade',
      'Transitions to garages, walks, and steps',
      'Traction, decorative finish, and maintenance',
      'Removal and haul-away of existing material',
    ],
    includes: [
      'Driveways and replacement driveways',
      'Patios and outdoor living slabs',
      'Walkways and concrete steps',
      'Small slabs and equipment pads',
      'Broom and decorative finishes',
      'Stamped, colored, and exposed aggregate options',
    ],
    approach: [
      {
        title: 'Review grade and intended use',
        text: 'The layout begins with access, drainage, expected loads, adjacent structures, and the finish you want to live with.',
      },
      {
        title: 'Build uniform support',
        text: 'Firm-looking soil is not treated as a finished base. Unsuitable material is addressed before the project-specific base, forms, and reinforcement are prepared.',
      },
      {
        title: 'Place, joint, finish, and cure',
        text: 'Concrete is finished for the intended use, joints are planned, and the slab is protected during early curing.',
      },
    ],
    projectStoryTitle: 'The finished concrete starts below the surface.',
    projectStoryIntro:
      'Actual project photographs show prepared support, a finished patio slab, a driveway installation, and a long access walk.',
    projectPhotos: [
      {
        title: 'Prepared base',
        caption: 'The patio area was excavated, formed, and built up with a compacted aggregate base before placement.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/concrete-patio-base-prep.webp',
        alt: 'Compacted aggregate base and forms prepared for a concrete patio',
        width: 1024,
        height: 768,
      },
      {
        title: 'Finished patio',
        caption: 'A new patio slab was finished with planned edges and transitions at the home.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/concrete-patio-finished.webp',
        alt: 'Finished concrete patio beside a home',
        width: 1024,
        height: 768,
      },
      {
        title: 'Driveway slab',
        caption: 'A broad concrete driveway and parking slab was placed for a new-construction property.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/concrete-driveway-slab.webp',
        alt: 'New concrete driveway and parking slab beside a house',
        width: 603,
        height: 452,
      },
      {
        title: 'Long access walk',
        caption: 'A continuous concrete walk was graded and finished along the building entrance.',
        location: 'Eastern North Carolina',
        image: '/images/owner-projects/concrete-long-sidewalk.webp',
        alt: 'Long finished concrete sidewalk following a building entrance',
        width: 603,
        height: 452,
      },
    ],
    faqs: [
      {
        q: 'Will a new driveway ever crack?',
        a: 'Concrete naturally shrinks as it hardens. Sound preparation and planned joints help control where cracking occurs, but an honest contractor should not promise crack-free concrete.',
      },
      {
        q: 'How soon can I walk or drive on new concrete?',
        a: 'Timing depends on the mix, weather, slab, and expected load. Follow the use schedule provided for your specific project.',
      },
      {
        q: 'Which finish works well for a driveway?',
        a: 'A broom finish is a practical, traction-oriented option. Decorative finishes can also work when texture, upkeep, and budget fit the project.',
      },
      {
        q: 'Can an old driveway be removed?',
        a: 'Demolition and haul-away can be included when the written scope calls for full replacement.',
      },
    ],
  },
  {
    slug: 'retaining-walls-drainage',
    name: 'Retaining Walls and Drainage',
    navName: 'Walls and Drainage',
    featured: false,
    footer: false,
    kicker: 'Treat the wall, soil, and water as one system.',
    summary:
      'Retaining walls, repairs, terraced systems, steps, capstones, and the grading and drainage that support the finished work.',
    intro:
      'A successful retaining wall is more than a row of attractive block. Soil, height, slope, nearby loads, groundwater, surface runoff, reinforcement, and a safe discharge point all matter.',
    image: '/images/owner-projects/landscape-retaining-border.webp',
    imageAlt: 'Curved segmental landscape wall completed in Eastern North Carolina',
    imagePosition: '50% 50%',
    concernsTitle: 'Warning signs around a retaining wall',
    concerns: [
      'The wall is leaning or bulging',
      'Units or capstones have shifted apart',
      'Soil is eroding at the base or ends',
      'Water stains or wet areas persist',
      'The drain outlet is missing, buried, or blocked',
      'Cracks return after an earlier patch',
    ],
    includes: [
      'Segmental block retaining walls',
      'Block and masonry retaining walls',
      'Terraced wall systems',
      'Wall repair and reinforcement',
      'Integrated steps and capstones',
      'Supporting grading, drainage, and outlet planning',
    ],
    approach: [
      {
        title: 'Understand the water',
        text: 'The first question is where surface and subsurface water comes from and where it can discharge safely.',
      },
      {
        title: 'Account for loads and soil',
        text: 'Wall height, slopes, driveways, structures, groundwater, and soil conditions influence the design.',
      },
      {
        title: 'Build the complete system',
        text: 'The prepared base, grading, wall units, backfill, drainage, outlets, and reinforcement must work together.',
      },
    ],
    faqs: [
      {
        q: 'Does every retaining wall need drainage?',
        a: 'Most walls need a plan for surface and subsurface water. The details and discharge point depend on the individual site.',
      },
      {
        q: 'When is engineering required?',
        a: 'Requirements depend on height, soil, slope, nearby loads, and local rules. Taller walls and walls supporting driveways, buildings, or steep slopes may require professional design.',
      },
      {
        q: 'Can a leaning wall be repaired?',
        a: 'Sometimes. The cause, movement, drainage, foundation, wall system, and remaining materials determine whether repair or reconstruction is appropriate.',
      },
      {
        q: 'Is gravel and pipe enough to fix runoff?',
        a: 'Not by itself. Drainage behind a wall handles incidental water and should not become the primary route for uncontrolled surface runoff.',
      },
    ],
  },
  {
    slug: 'driveway-culverts-headwalls',
    name: 'Driveway Culverts and Stone Headwalls',
    navName: 'Culverts',
    featured: false,
    footer: false,
    kicker: 'Keep the entrance usable and the drainage path open.',
    summary:
      'Culvert and entrance work, stone headwalls, wingwalls, erosion repair, compacted backfill, and outlet protection.',
    intro:
      'Driveway culverts carry ditch and stormwater beneath an entrance. Headwalls and wingwalls can retain surrounding fill, protect the opening, reduce erosion exposure, and give the entrance a finished appearance.',
    image: '/images/owner-projects/culvert-headwall.webp',
    imageAlt: 'Stone-faced headwall completed around a driveway culvert',
    imagePosition: '50% 62%',
    concernsTitle: 'What the site review should cover',
    concerns: [
      'Pipe condition, alignment, and available cover',
      'Ditch flow and driveway grade',
      'Erosion at the inlet, outlet, or shoulders',
      'Compaction around the existing crossing',
      'A safe discharge path downstream',
      'Public right-of-way or environmental review',
    ],
    includes: [
      'Driveway culvert installation and replacement',
      'Natural and manufactured stone headwalls',
      'Wingwalls and retained fill',
      'Compacted backfill and entrance restoration',
      'Outlet protection and erosion repair',
      'Coordination-ready scopes for regulated sites',
    ],
    approach: [
      {
        title: 'Confirm hydraulic function first',
        text: 'A decorative headwall cannot correct a damaged, undersized, poorly aligned, or improperly graded culvert.',
      },
      {
        title: 'Stabilize the crossing',
        text: 'Pipe support, backfill placement, compaction, cover, and the transition to the drive are planned together.',
      },
      {
        title: 'Finish and protect the opening',
        text: 'Headwalls, wingwalls, and outlet protection are fitted to the site after the drainage path is established.',
      },
    ],
    faqs: [
      {
        q: 'What does a stone headwall do?',
        a: 'It finishes and stabilizes the area around a culvert opening and can help protect adjacent fill from erosion.',
      },
      {
        q: 'Can a headwall be added to an existing pipe?',
        a: 'Sometimes, but the pipe condition, size, alignment, cover, and drainage performance should be reviewed first.',
      },
      {
        q: 'Who determines the culvert size?',
        a: 'Sizing depends on expected flow and site conditions. Work in a public right-of-way may also require NCDOT or local approval.',
      },
      {
        q: 'Can erosion at the outlet be repaired?',
        a: 'Outlet protection can be included, but the cause of the erosion must also be addressed.',
      },
    ],
  },
  {
    slug: 'stone-veneer',
    name: 'Manufactured Stone Veneer and Faux Stone',
    seoTitle: "Stone Veneer for High-End Homes in Eastern NC | Vic's Bricks",
    navName: 'Faux Stone',
    featured: true,
    footer: true,
    kicker: 'Finish the visible stone and the water-management details behind it.',
    summary:
      'Manufactured stone veneer and faux-stone accents for high-end homes in Eastern NC, with careful attention to entries, columns, color, and finish.',
    intro:
      'Manufactured stone veneer brings architectural character to high-end homes through carefully balanced proportions, colors, corners, and mortar joints. Entries, columns, and exterior elevations should feel cohesive with the property. Long-term performance also depends on the wall assembly behind the stone.',
    image: '/images/owner-projects/faux-stone-new-construction.webp',
    imageAlt: 'Manufactured stone veneer installed on a new-construction exterior',
    imagePosition: '50% 62%',
    concernsTitle: 'Architectural stone accents for high-end homes',
    concerns: [
      'Prepared and compatible substrate',
      'Water-resistive layers and flashing',
      'Weep and drainage details',
      'Corner transitions and trim',
      'Stone proportions and color blending',
      'Joint style and mortar finish',
    ],
    includes: [
      'Manufactured stone veneer',
      'Faux-stone accent walls and entries',
      'Columns and architectural accents',
      'Outdoor living and fireplace surrounds',
      'Corners, trim, and joint finishing',
      'Repairs to selected veneer areas',
    ],
    approach: [
      {
        title: 'Review the wall assembly',
        text: 'The substrate, weather barrier, flashing, drainage, and transitions must support the veneer system.',
      },
      {
        title: 'Confirm the visual mix',
        text: 'Samples or a small layout area can confirm stone scale, color balance, corners, and joint appearance.',
      },
      {
        title: 'Install as a complete cladding',
        text: 'The stone, mortar bond, joints, openings, drainage, and adjacent materials are detailed as one assembly.',
      },
    ],
    faqs: [
      {
        q: 'Is stone veneer waterproof?',
        a: 'Stone is one part of the exterior wall. Water-resistive barriers, flashing, drainage, and properly detailed openings manage incidental water behind it.',
      },
      {
        q: 'What is manufactured stone veneer?',
        a: 'Manufactured stone veneer is a lightweight masonry-facing product designed to create the appearance and texture of stone in a properly detailed wall assembly.',
      },
      {
        q: 'Can veneer go over an existing wall?',
        a: 'The existing substrate and wall assembly must first be evaluated for soundness, compatibility, and water-management requirements.',
      },
      {
        q: 'Can I approve the stone pattern first?',
        a: 'A sample or mock-up is a useful way to review blend, scale, joint style, and corners before broad installation.',
      },
    ],
  },
  {
    slug: 'chimney-masonry-repair',
    name: 'Selected Exterior Chimney Masonry Repair',
    navName: 'Chimney Repair',
    featured: false,
    footer: false,
    kicker: 'Case-by-case exterior masonry work based on scope and safe access.',
    summary:
      'Selected exterior chimney brick and mortar repairs considered case by case based on the work and access.',
    intro:
      'Exterior chimney masonry can be affected by deteriorated mortar, loose or damaged brick, and water entry. Vic’s Bricks reviews these projects individually and confirms the exterior masonry work included in the written scope.',
    image: '/images/owner-projects/chimney-blue-sky.webp',
    imageAlt: 'Brick chimney masonry photographed against a blue sky',
    imagePosition: '50% 34%',
    concernsTitle: 'Exterior chimney warning signs',
    concerns: [
      'Loose, cracked, or flaking brick',
      'Open or deteriorated mortar joints',
      'A cracked or poorly shedding crown',
      'Failed roof-to-chimney flashing',
      'Water staining near the fireplace',
      'A missing or damaged chimney cap',
    ],
    includes: [
      'Selected exterior chimney repointing',
      'Selected exterior brick replacement',
      'Exterior masonry condition review',
      'Access and scope evaluation',
      'Clearly defined masonry responsibilities',
      'Clear separation from flue and appliance work',
    ],
    approach: [
      {
        title: 'Review every exterior water path',
        text: 'Water staining does not identify the source by itself. Crown, cap, flashing, brick, mortar, and roof transitions all matter.',
      },
      {
        title: 'Separate masonry from appliance safety',
        text: 'Exterior repair does not replace inspection of a flue, liner, appliance, or combustion system.',
      },
      {
        title: 'Repair the written scope',
        text: 'Brick, mortar, crown, cap, and flashing responsibilities are spelled out so the exterior work is clear.',
      },
    ],
    faqs: [
      {
        q: 'Can damaged chimney brick be replaced?',
        a: 'Often, yes. The extent of damage, access, matching material, and condition of the surrounding masonry guide the repair.',
      },
      {
        q: 'Does exterior masonry repair inspect the flue?',
        a: 'No. Flues, liners, appliances, and combustion systems should be evaluated by a qualified chimney professional.',
      },
      {
        q: 'Where can chimney water enter?',
        a: 'Possible paths include the crown, cap, mortar joints, brick, flashing, and roof-to-chimney transition.',
      },
      {
        q: 'What should I do after a suspected chimney fire?',
        a: 'Do not use the fireplace until a qualified chimney professional has inspected the system.',
      },
    ],
  },
];

services.unshift({
  slug: 'paver-driveways-hardscapes',
  name: 'Paver Driveways and Hardscapes',
  heading: 'Premium Paver Driveways, Patios, and Hardscapes',
  seoTitle: "Premium Paver Patios & Driveways in Eastern NC | Vic's Bricks",
  navName: 'Pavers',
  featured: true,
  footer: true,
  kicker: 'Shape durable outdoor spaces with pattern, edges, and drainage in mind.',
  summary:
    'Premium paver patios, driveways, and walkways in Eastern NC, with thoughtful patterns, clean borders, careful base preparation, and drainage planning.',
  intro:
    'Pavers can define a driveway, connect outdoor living spaces, or bring a more detailed finish to a garden edge. For a high-end finish, the details matter: coordinated colors, deliberate patterns, clean borders, and thoughtful transitions to the home and garden. Long-term performance starts below the surface with careful base preparation, edge restraint, joints, and water movement.',
  image: '/images/owner-projects/multicolor-paver-walkway.webp',
  imageAlt: 'Multicolor brick paver walkway installed in Eastern North Carolina',
  imagePosition: '50% 50%',
  concernsTitle: 'Decisions that shape a paver project',
  concerns: [
    'Vehicle loads and turning paths',
    'Excavation depth and base preparation',
    'Surface pitch and drainage outlets',
    'Edge restraint and transitions',
    'Paver scale, pattern, color, and texture',
    'Repair access and long-term maintenance',
  ],
  includes: [
    'Paver driveways and entrance courts',
    'Patios and outdoor living areas',
    'Walkways and garden paths',
    'Landscape borders and curved edges',
    'Steps and hardscape transitions',
    'Selective paver repair and reset',
  ],
  approach: [
    {
      title: 'Lay out movement and water',
      text: 'The pattern is planned around how people and vehicles use the space and where runoff can move safely.',
    },
    {
      title: 'Prepare the support system',
      text: 'Excavation, base material, compaction, bedding, and edge restraint are fitted to the intended use rather than relying on firm-looking native ground.',
    },
    {
      title: 'Set the field and finish the joints',
      text: 'Pavers are aligned, cut at transitions, restrained at the edges, and finished with the specified joint material.',
    },
  ],
  projectStoryTitle: 'Premium paver details, shown in place.',
  projectStoryIntro:
    'These completed projects show three different uses of modular paving: a multicolor walk, a vehicle-rated driveway, and an outdoor living patio.',
  projectPhotos: [
    {
      title: 'Multicolor walkway',
      caption: 'A blended brick-paver field creates a durable garden walk with a varied, established character.',
      location: 'Eastern North Carolina',
      image: '/images/owner-projects/multicolor-paver-walkway.webp',
      alt: 'Completed multicolor brick paver walkway through a landscaped yard',
      width: 1000,
      height: 750,
    },
    {
      title: 'Paver driveway',
      caption: 'A complete paver driveway replaced the previous grass-grid parking surface.',
      location: 'Eastern North Carolina',
      image: '/images/owner-projects/paver-driveway-finished.webp',
      alt: 'Finished light-colored paver driveway beside a home',
      width: 603,
      height: 452,
    },
    {
      title: 'Paver patio',
      caption: 'A broad paver patio extends the usable outdoor space behind the home.',
      location: 'Eastern North Carolina',
      image: '/images/owner-projects/paver-patio-finished.webp',
      alt: 'Finished paver patio behind a home',
      width: 603,
      height: 452,
    },
  ],
  faqs: [
    {
      q: 'Can pavers be used for a driveway?',
      a: 'Yes, when the paver system, base, edge restraint, layout, and drainage are designed for vehicle use.',
    },
    {
      q: 'Can individual pavers be repaired?',
      a: 'One advantage of modular paving is that selected areas can often be lifted and reset after the underlying cause is corrected.',
    },
    {
      q: 'How should a paver area drain?',
      a: 'The surface should be graded toward an appropriate discharge path without directing water toward foundations or neighboring property.',
    },
    {
      q: 'What determines the pattern?',
      a: 'Paver shape, intended load, field dimensions, edge conditions, transitions, and the desired visual rhythm all play a role.',
    },
  ],
});

services.splice(
  2,
  0,
  {
    slug: 'brick-block-masonry',
    name: 'Brick and Block Masonry',
    navName: 'Brick and Block',
    featured: true,
    footer: true,
    kicker: 'Build or repair masonry with clean lines and a well-defined scope.',
    summary:
      'New brick and concrete block installations, repairs, and masonry features for homes and properties across Eastern North Carolina.',
    intro:
      'Brick and concrete block can serve structural, practical, and decorative roles around a property. Vic’s Bricks handles both new installations and repairs, with the materials, adjoining surfaces, and finished appearance considered together.',
    image: '/images/owner-projects/new-construction-house-chimney.webp',
    imageAlt: 'Wide view of a house and brick chimney project under construction',
    imagePosition: '50% 50%',
    concernsTitle: 'Start with the purpose of the masonry',
    concerns: [
      'New construction or repair to existing work',
      'Brick, concrete block, or a combination of materials',
      'Visible finish and joint appearance',
      'Connections to existing walls and surfaces',
      'Movement, cracking, or earlier patchwork',
      'Access, drainage, surrounding grade, and footing conditions',
    ],
    includes: [
      'New brick masonry',
      'New concrete block masonry',
      'Brick and block repairs',
      'Walls and property features',
      'Transitions to existing masonry',
      'Joint and finish detailing',
    ],
    approach: [
      {
        title: 'Define what the masonry needs to do',
        text: 'Use, location, loads, exposure, and the desired appearance shape the material and project scope.',
      },
      {
        title: 'Review adjoining construction',
        text: 'Existing walls, openings, foundations, drainage, finish transitions, and any required footing are considered before the work begins.',
      },
      {
        title: 'Build for a cohesive finish',
        text: 'Layout, bond, joint profile, and material selection are planned so the completed work belongs with the property.',
      },
    ],
    faqs: [
      {
        q: 'Do you handle both new masonry and repairs?',
        a: 'Yes. Vic’s Bricks considers new brick and block installations as well as repairs to existing masonry.',
      },
      {
        q: 'Can new masonry be matched to existing work?',
        a: 'Available brick or block, mortar color, sand texture, bond, and joint profile are reviewed to create a compatible transition.',
      },
      {
        q: 'When might plans or engineering be needed?',
        a: 'Requirements depend on the purpose, loads, height, location, and local rules. Work with structural implications may require plans or professional review.',
      },
      {
        q: 'Can you repair an earlier masonry patch?',
        a: 'Often, but the existing materials and the reason the earlier work failed should be evaluated before a new repair is recommended.',
      },
    ],
  },
  {
    slug: 'indoor-outdoor-tile-installation',
    name: 'Indoor and Outdoor Tile Installation',
    seoTitle: "Premium Indoor & Outdoor Tile in Eastern NC | Vic's Bricks",
    navName: 'Tile',
    featured: true,
    footer: true,
    kicker: 'Bring deliberate layout and durable finishes to interior and exterior spaces.',
    summary:
      'Premium indoor and outdoor tile installation in Eastern NC, with balanced layouts, clean edges, coordinated grout, and carefully planned transitions.',
    intro:
      'A premium tile finish starts with thoughtful layout: balanced cuts, clean edges, coordinated grout, and carefully planned transitions to adjoining surfaces. The substrate, indoor or outdoor setting, material, and expected use shape the installation before the first piece is set.',
    image: '/images/owner-projects/tile-backsplash-finished.webp',
    imageAlt: 'Finished tile backsplash installed around a kitchen range',
    imagePosition: '50% 50%',
    concernsTitle: 'Details behind a premium tile finish',
    concerns: [
      'Indoor or outdoor exposure',
      'Condition and flatness of the substrate',
      'Tile size, pattern, and orientation',
      'Edges, corners, and adjoining finishes',
      'Movement joints and changes in plane',
      'Replacement material for selected repairs',
    ],
    includes: [
      'Interior floor and wall tile',
      'Backsplashes and accent areas',
      'Outdoor tile surfaces',
      'Selected tile replacement and repair',
      'Pattern, edge, and transition planning',
      'Grout and finish detailing',
    ],
    approach: [
      {
        title: 'Confirm the setting and material',
        text: 'The tile and installation materials should fit the substrate, exposure, expected traffic, and maintenance needs.',
      },
      {
        title: 'Plan the visible layout',
        text: 'Reference lines, cuts, focal areas, transitions, and edge details are considered before broad installation begins.',
      },
      {
        title: 'Finish the complete surface',
        text: 'Tile, joints, edges, transitions, and adjoining materials are completed as one coordinated finish.',
      },
    ],
    faqs: [
      {
        q: 'Do you install tile indoors and outdoors?',
        a: 'Yes. The material and installation approach are selected for the specific interior or exterior setting.',
      },
      {
        q: 'Can tile be installed over an existing surface?',
        a: 'The existing surface must first be checked for soundness, flatness, movement, and compatibility with the proposed installation.',
      },
      {
        q: 'Can a damaged tile be replaced?',
        a: 'Selected repairs may be possible when matching material is available and the surrounding installation remains sound.',
      },
      {
        q: 'What makes an outdoor tile project different?',
        a: 'Outdoor exposure adds considerations such as water movement, temperature changes, slip resistance, substrate conditions, and exterior-rated materials.',
      },
    ],
  },
);

export const serviceAreaGroups = [
  {
    county: 'Craven County',
    places: ['Havelock', 'New Bern'],
    context:
      'Brick homes, outdoor living areas, drainage improvements, and concrete work across the Havelock and New Bern area.',
  },
  {
    county: 'Carteret County',
    places: ['Morehead City', 'Beaufort'],
    context:
      'Coastal exposure makes moisture paths, flashing, material compatibility, and water management especially important.',
  },
  {
    county: 'Onslow County',
    places: ['Jacksonville'],
    context:
      'Residential masonry repair, concrete flatwork, entrances, and retaining solutions for growing properties and established homes.',
  },
  {
    county: 'Jones, Pamlico, and Pitt Counties',
    places: ['Trenton', 'Bayboro', 'Greenville'],
    context:
      'Project availability depends on location, access, scope, and the current schedule. Contact Vic’s Bricks to confirm service for your property.',
  },
];

export const projects = [
  {
    title: 'New-construction chimney and masonry',
    category: 'Brick and block',
    location: 'Eastern North Carolina',
    caption: 'Chimney masonry and exterior brickwork completed as part of a larger new-construction project.',
    image: '/images/owner-projects/new-construction-house-chimney.webp',
    alt: 'Wide view of new-construction chimney and exterior masonry work',
    imagePosition: '50% 50%',
  },
  {
    title: 'Multicolor paver walkway',
    category: 'Pavers and hardscape',
    location: 'Eastern North Carolina',
    caption: 'A blended brick-paver walkway laid through an established landscape with a clean transition at the drive.',
    image: '/images/owner-projects/multicolor-paver-walkway.webp',
    alt: 'Finished multicolor brick paver walkway through a landscaped yard',
    imagePosition: '50% 50%',
  },
  {
    title: 'Concrete patio from base to finish',
    category: 'Concrete',
    location: 'Eastern North Carolina',
    caption: 'A new patio slab installed after excavation, forming, and compacted aggregate base preparation.',
    image: '/images/owner-projects/concrete-patio-finished.webp',
    alt: 'Finished concrete patio beside an Eastern North Carolina home',
    imagePosition: '50% 50%',
  },
  {
    title: 'Wider door opening and brick restoration',
    category: 'Brick repair',
    location: 'Eastern North Carolina',
    caption: 'The brick surround was selectively opened, supported, and rebuilt around a wider exterior door system.',
    image: '/images/owner-projects/door-opening-finished.webp',
    alt: 'Completed brick surround around a widened exterior door opening',
    imagePosition: '50% 42%',
  },
  {
    title: 'Paver driveway installation',
    category: 'Pavers and hardscape',
    location: 'Eastern North Carolina',
    caption: 'A complete paver field creates a durable vehicle surface and a finished approach to the home.',
    image: '/images/owner-projects/paver-driveway-finished.webp',
    alt: 'Finished light-colored paver driveway beside a home',
    imagePosition: '50% 50%',
  },
  {
    title: 'Finished paver patio',
    category: 'Pavers and hardscape',
    location: 'Eastern North Carolina',
    caption: 'A broad paver patio extends the outdoor living space behind the home with a clean transition to the lawn.',
    image: '/images/owner-projects/paver-patio-finished.webp',
    alt: 'Finished paver patio behind an Eastern North Carolina home',
    imagePosition: '50% 50%',
  },
  {
    title: 'Long concrete access walk',
    category: 'Concrete',
    location: 'Eastern North Carolina',
    caption: 'A continuous concrete sidewalk was graded and finished to connect the building entrances.',
    image: '/images/owner-projects/concrete-long-sidewalk.webp',
    alt: 'Long finished concrete access walk beside a building',
    imagePosition: '50% 50%',
  },
  {
    title: 'Curved landscape retaining border',
    category: 'Walls and drainage',
    location: 'Eastern North Carolina',
    caption: 'A low segmental wall defines the planting bed while following the home’s curved landscape line.',
    image: '/images/owner-projects/landscape-retaining-border.webp',
    alt: 'Curved segmental landscape wall surrounding a planted bed',
    imagePosition: '50% 50%',
  },
  {
    title: 'Kitchen tile backsplash',
    category: 'Indoor tile',
    location: 'Eastern North Carolina',
    caption: 'A clean running-bond backsplash was fitted around the range, outlets, cabinets, and counters.',
    image: '/images/owner-projects/tile-backsplash-finished.webp',
    alt: 'Finished kitchen tile backsplash around a range and countertops',
    imagePosition: '50% 50%',
  },
  {
    title: 'Stone-faced culvert headwall',
    category: 'Drainage and masonry',
    location: 'Eastern North Carolina',
    caption: 'A compact headwall finishes and stabilizes the driveway culvert opening.',
    image: '/images/owner-projects/culvert-headwall.webp',
    alt: 'Stone-faced headwall around a driveway culvert',
    imagePosition: '50% 50%',
  },
  {
    title: 'New-construction faux-stone veneer',
    category: 'Stone veneer',
    location: 'Eastern North Carolina',
    caption: 'Manufactured stone veneer was laid across the lower exterior elevation with fitted transitions at openings and corners.',
    image: '/images/owner-projects/faux-stone-new-construction.webp',
    alt: 'Manufactured stone veneer installed along a new-construction exterior',
    imagePosition: '50% 50%',
  },
];

export const guides = [
  {
    slug: 'when-brick-mortar-needs-repointing',
    title: 'When Brick Mortar Needs Repointing',
    description:
      'Learn what deteriorated mortar looks like, why compatibility matters, and when movement calls for a broader evaluation.',
    readTime: '6 minute read',
    image: '/images/owner-projects/brick-steps-finished.webp',
    imageAlt: 'Finished brick steps with clean mortar joints',
    sections: [
      {
        heading: 'Start with the condition of the joint',
        body: [
          'Mortar is intended to weather, but open joints can admit more water and accelerate damage. Common warning signs include crumbling surfaces, missing material, cracks, and joints that have eroded back from the face of the brick.',
          'A repair should reach sound mortar. Smearing a thin layer over loose material may hide the problem briefly without restoring the joint.',
        ],
      },
      {
        heading: 'Compatibility matters',
        body: [
          'New mortar should work with the existing brick and masonry. Mortar that is much harder than older, softer brick can transfer stress into the units and contribute to cracking or flaking.',
          'Color is only one part of a match. Sand, texture, hardness, and the finished joint profile also affect the result.',
        ],
      },
      {
        heading: 'Look for the water source',
        body: [
          'Repointing alone may not solve a recurring leak. Flashing, sealants, caps, weeps, roof transitions, and site drainage can all influence how water reaches the wall.',
          'Bulging, substantial movement, and cracks through the brick may need professional structural evaluation before surface repairs begin.',
        ],
      },
    ],
  },
  {
    slug: 'concrete-driveway-planning-guide',
    title: 'A Practical Concrete Driveway Planning Guide',
    description:
      'The site, base, drainage, joints, finish, and curing questions worth resolving before a driveway is placed.',
    readTime: '7 minute read',
    image: '/images/owner-projects/concrete-patio-base-prep.webp',
    imageAlt: 'Compacted aggregate base and forms prepared for a concrete patio',
    sections: [
      {
        heading: 'Plan around use and drainage',
        body: [
          'The driveway should account for vehicle paths, parking, garage transitions, existing walks, nearby grades, and where rainwater can move without creating a new problem.',
          'Drainage planning is especially important in low-relief parts of Eastern North Carolina where water may leave a site slowly.',
        ],
      },
      {
        heading: 'Uniform support starts below the slab',
        body: [
          'Soft areas, disturbed soil, roots, and inconsistent base thickness can create uneven support. Firm-looking ground is not a substitute for the prepared base specified for the project.',
          'The written scope should also say whether old concrete, unsuitable material, and excess soil are removed from the property.',
        ],
      },
      {
        heading: 'Joints and curing are part of the work',
        body: [
          'Concrete naturally changes volume as it hardens. Planned joints encourage shrinkage cracks to form in intended locations.',
          'Curing protects the moisture and temperature conditions that concrete needs to develop strength. Walking and vehicle timing should come from the mix, weather, slab, and expected load on the actual project.',
        ],
      },
    ],
  },
  {
    slug: 'retaining-wall-drainage-basics',
    title: 'Why Retaining Wall Drainage Is Site-Specific',
    description:
      'A homeowner-friendly look at runoff, backfill, outlets, reinforcement, and the loads a wall may need to support.',
    readTime: '6 minute read',
    image: '/images/owner-projects/landscape-retaining-border.webp',
    imageAlt: 'Finished curved segmental landscape wall',
    sections: [
      {
        heading: 'A wall changes how water moves',
        body: [
          'A retaining wall interrupts the natural path of surface and subsurface water. The site plan should identify where that water comes from and a stable place for it to discharge.',
          'Drainage material behind a wall can manage incidental water. It should not become the main route for uncontrolled roof, driveway, or uphill runoff.',
        ],
      },
      {
        heading: 'Height is only one design factor',
        body: [
          'Soil, slope, groundwater, wall geometry, nearby structures, and vehicle loads can matter as much as visible height.',
          'Local permit and engineering requirements apply. Walls supporting driveways, buildings, steep slopes, or other surcharge loads may need professional design.',
        ],
      },
      {
        heading: 'Repair the cause of movement',
        body: [
          'Leaning, bulging, separated units, erosion, and recurring cracks are symptoms. A lasting repair depends on the base, drainage, backfill, reinforcement, and site water that caused the movement.',
        ],
      },
    ],
  },
];

export function getService(slug) {
  return services.find((service) => service.slug === slug);
}

export function getGuide(slug) {
  return guides.find((guide) => guide.slug === slug);
}
